import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Sparkles, Check, Loader2, Crown, Receipt, Users, Download, ShieldCheck, XCircle, RotateCcw, CreditCard, Calculator,
} from "lucide-react";
import EmbeddedCheckoutModal from "@/components/EmbeddedCheckoutModal";
import DonorCRM from "@/components/pro/DonorCRM";

const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function ProManager() {
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);
  const [params, setParams] = useSearchParams();
  const [activating, setActivating] = useState(false);

  const loadMe = useCallback(() => api.get("/pro/me").then((r) => setMe(r.data)).catch(() => setMe(null)), []);

  useEffect(() => {
    loadMe();
    api.get("/pro/plans").then((r) => setPlans(r.data)).catch(() => setPlans(null));
  }, [loadMe]);

  // Return-from-Stripe activation
  useEffect(() => {
    const proParam = params.get("pro");
    const sid = params.get("session_id");
    if (proParam === "success" && sid) {
      setActivating(true);
      let tries = 0;
      const tick = async () => {
        try {
          const { data } = await api.get(`/pro/status/${sid}`);
          if (data.is_pro) {
            toast.success("Welcome to FightFund Pro! Your tools are unlocked.");
            await loadMe();
            setActivating(false);
            setParams({}, { replace: true });
            return;
          }
        } catch (_) {}
        if (++tries >= 10) { setActivating(false); setParams({}, { replace: true }); return; }
        setTimeout(tick, 2000);
      };
      tick();
    } else if (proParam === "cancel") {
      toast.info("Checkout canceled — you can upgrade anytime.");
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!me) return <div className="mt-8"><InlineLoader label="Loading FightFund Pro" /></div>;

  return (
    <div className="mt-10" data-testid="pro-manager">
      <div className="mb-4 flex items-center gap-2">
        <Crown className="h-5 w-5 text-ff-amber" />
        <h2 className="font-heading text-xl font-extrabold text-ff-blue"><Brand /> Pro</h2>
        {me.is_pro && <span data-testid="pro-active-chip" className="rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-xs font-bold text-[#92400E]">ACTIVE</span>}
      </div>

      {activating ? (
        <div className="rounded-2xl border border-ff-border bg-white p-8 text-center shadow-sm" data-testid="pro-activating">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-ff-red" />
          <div className="font-heading font-bold text-ff-blue">Activating your subscription…</div>
        </div>
      ) : me.is_pro ? (
        <div className="space-y-6">
          <ProStatus me={me} reload={loadMe} />
          <ReceiptBranding me={me} reload={loadMe} />
          <DonorCRM />
          <RecurringDonors />
        </div>
      ) : (
        <Pricing plans={plans} />
      )}
    </div>
  );
}

function Pricing({ plans }) {
  const [plan, setPlan] = useState("annual");
  const [busy, setBusy] = useState(false);
  const [raise, setRaise] = useState(2500);
  const [stripePk, setStripePk] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const list = plans?.plans || [];
  const benefits = plans?.benefits || [];
  const stdPct = plans?.fees?.platform_fee_pct ?? 5;
  const proPct = plans?.fees?.pro_fee_pct ?? 3;

  useEffect(() => {
    api.get("/settings/public").then((r) => { if (r.data.stripe_publishable_key) setStripePk(r.data.stripe_publishable_key); }).catch(() => {});
  }, []);

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pro/checkout", { plan, origin_url: window.location.origin });
      setClientSecret(data.client_secret);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };

  // "Pro pays for itself" math
  const monthlyCost = plan === "annual" ? 99 / 12 : 9.95;
  const monthlySavings = raise * (stdPct - proPct) / 100;
  const net = monthlySavings - monthlyCost;
  const feeDeltaPct = (stdPct - proPct) / 100;
  const breakeven = feeDeltaPct > 0 ? Math.ceil(monthlyCost / feeDeltaPct) : null;

  return (
    <div className="overflow-hidden rounded-2xl border border-ff-border bg-white shadow-sm" data-testid="pro-pricing">
      <div className="grid gap-0 md:grid-cols-2">
        <div className="bg-ff-blue p-8 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="h-3.5 w-3.5" /> Upgrade
          </div>
          <h3 className="mt-4 font-heading text-2xl font-extrabold">Do more with Pro</h3>
          <p className="mt-2 text-sm text-white/70">Branded tax receipts, donor retention tools, priority review and a trust badge — built for serious fundraisers and nonprofits.</p>
          <ul className="mt-6 space-y-3">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-white/90">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-ff-green" /> <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-8">
          <div className="grid grid-cols-2 gap-3">
            {list.map((p) => (
              <button key={p.id} onClick={() => setPlan(p.id)} data-testid={`plan-${p.id}`}
                className={`rounded-xl border-2 p-4 text-left transition-colors duration-200 ${plan === p.id ? "border-ff-red bg-[#FEF2F2]" : "border-ff-border hover:border-ff-blue"}`}>
                <div className="text-xs font-bold uppercase tracking-wide text-ff-medium">{p.label}</div>
                <div className="mt-1 font-heading text-2xl font-extrabold text-ff-blue">
                  ${p.amount}<span className="text-sm font-semibold text-ff-medium">/{p.interval === "year" ? "yr" : "mo"}</span>
                </div>
                {p.savings && <div className="mt-1 text-[11px] font-bold text-ff-green">{p.savings}</div>}
              </button>
            ))}
          </div>

          {/* Pro pays for itself calculator */}
          <div className="mt-5 rounded-xl border border-ff-border bg-[#F9FAFB] p-4" data-testid="pro-calculator">
            <div className="flex items-center gap-2 text-sm font-bold text-ff-blue">
              <Calculator className="h-4 w-4" /> Does Pro pay for itself?
            </div>
            <div className="mt-3 flex items-center justify-between text-xs font-semibold text-ff-medium">
              <span>You raise per month</span>
              <span className="font-heading text-base font-extrabold text-ff-blue" data-testid="calc-raise-value">{money(raise)}</span>
            </div>
            <input type="range" min="100" max="20000" step="100" value={raise} data-testid="calc-raise-input"
              onChange={(e) => setRaise(Number(e.target.value))}
              className="mt-2 w-full accent-ff-red" />
            <div className="mt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-ff-medium"><span>Standard fee ({stdPct}%)</span><span className="font-semibold text-ff-red">− {money(raise * stdPct / 100)}/mo</span></div>
              <div className="flex justify-between text-ff-medium"><span>Pro fee ({proPct}%)</span><span className="font-semibold text-ff-green">− {money(raise * proPct / 100)}/mo</span></div>
              <div className="flex justify-between border-t border-ff-border pt-1.5 text-ff-dark"><span>Fee savings</span><span className="font-bold text-ff-green" data-testid="calc-savings">{money(monthlySavings)}/mo</span></div>
              <div className="flex justify-between text-ff-dark"><span>Pro cost ({plan === "annual" ? "annual plan" : "monthly plan"})</span><span className="font-semibold">− {money(monthlyCost)}/mo</span></div>
            </div>
            <div className={`mt-3 rounded-lg p-3 text-center text-sm font-bold ${net >= 0 ? "bg-[#ECFDF5] text-ff-green" : "bg-[#FEF2F2] text-ff-red"}`} data-testid="calc-verdict">
              {net >= 0
                ? <>Pro pays for itself — you net <span className="font-extrabold">{money(net)}/mo</span> ahead 🎉</>
                : <>Raise <span className="font-extrabold">{money(breakeven)}/mo</span> and Pro pays for itself.</>}
            </div>
          </div>

          <Button onClick={subscribe} disabled={busy} data-testid="pro-subscribe-button"
            className="mt-5 w-full bg-ff-red font-semibold text-white hover:bg-ff-red/90">
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Opening checkout…</> : <><Crown className="mr-2 h-4 w-4" /> Subscribe to Pro</>}
          </Button>
          <p className="mt-3 text-center text-xs text-ff-medium">Secure checkout via Stripe. Cancel anytime.</p>
        </div>
      </div>

      <EmbeddedCheckoutModal
        open={!!clientSecret}
        onClose={() => { setClientSecret(""); setBusy(false); }}
        clientSecret={clientSecret}
        publishableKey={stripePk}
        title="Subscribe to FightFund Pro"
      />
    </div>
  );
}

function ProStatus({ me, reload }) {
  const [busy, setBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const scheduledCancel = me.cancel_at_period_end;

  useEffect(() => { api.get("/pro/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => setInvoices([])); }, []);

  const act = async (path, okMsg) => {
    setBusy(true);
    try { await api.post(path); toast.success(okMsg); await reload(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/pro/portal", { origin_url: window.location.origin });
      if (data.available && data.url) { window.location.href = data.url; return; }
      toast.info("Billing portal isn't available in this environment — use Cancel/Resume here. Invoices are listed below.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPortalBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="pro-status-card">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FEF3C7] text-[#92400E]"><Crown className="h-5 w-5" /></span>
          <div>
            <div className="font-heading font-bold text-ff-blue">Pro — {me.plan === "annual" ? "Annual" : "Monthly"} plan</div>
            <div className="text-sm text-ff-medium" data-testid="pro-renewal-line">
              {scheduledCancel
                ? (me.current_period_end
                    ? <>Cancels on <span className="font-semibold text-ff-red">{fmtDay(me.current_period_end)}</span> — Pro tools stay unlocked until then.</>
                    : <>Scheduled to cancel at the end of the current period — Pro tools stay unlocked until then.</>)
                : (me.current_period_end
                    ? <>Renews on <span className="font-semibold text-ff-blue">{fmtDay(me.current_period_end)}</span>.</>
                    : <>Renews automatically at the end of each billing period.</>)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" disabled={portalBusy} data-testid="pro-portal-button" onClick={openPortal}
            className="border-ff-border text-ff-blue hover:border-ff-blue">
            {portalBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" />} Manage billing
          </Button>
          {scheduledCancel ? (
            <Button variant="outline" disabled={busy} data-testid="pro-resume-button"
              onClick={() => act("/pro/resume", "Subscription resumed.")}
              className="border-ff-green text-ff-green hover:bg-ff-green hover:text-white">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />} Resume
            </Button>
          ) : (
            <Button variant="outline" disabled={busy} data-testid="pro-cancel-button"
              onClick={() => act("/pro/cancel", "Subscription will cancel at period end.")}
              className="border-ff-border text-ff-medium hover:border-ff-red hover:text-ff-red">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />} Cancel
            </Button>
          )}
        </div>
      </div>

      {invoices && invoices.length > 0 && (
        <div className="mt-5 border-t border-ff-border pt-4" data-testid="pro-invoices">
          <div className="mb-2 text-xs font-bold uppercase tracking-wide text-ff-medium">Invoice history</div>
          {invoices.map((inv, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm" data-testid={`invoice-row-${i}`}>
              <span className="text-ff-dark">{inv.number || "Invoice"} · {inv.created ? new Date(inv.created * 1000).toLocaleDateString() : ""}</span>
              <span className="flex items-center gap-3">
                <span className="font-semibold text-ff-blue">${inv.amount_paid}</span>
                {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-ff-blue underline">View</a>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReceiptBranding({ me, reload }) {
  const s = me.receipt_settings || {};
  const [form, setForm] = useState({
    org_name: s.org_name || "", logo_url: s.logo_url || "",
    thank_you_message: s.thank_you_message || "", ein: s.ein || "",
    tax_line_enabled: !!s.tax_line_enabled,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.patch("/pro/receipt-settings", form); toast.success("Receipt branding saved."); await reload(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="pro-receipt-branding">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#1D4ED8]"><Receipt className="h-5 w-5" /></span>
        <div>
          <div className="font-heading font-bold text-ff-blue">Custom receipt branding</div>
          <div className="text-sm text-ff-medium">Applied to every donor's tax-deductible receipt.</div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="brand-org">Organization name</Label>
          <Input id="brand-org" data-testid="brand-org-name" value={form.org_name} onChange={(e) => set("org_name", e.target.value)} placeholder="Your nonprofit's legal name" />
        </div>
        <div>
          <Label htmlFor="brand-ein">EIN (tax ID)</Label>
          <Input id="brand-ein" data-testid="brand-ein" value={form.ein} onChange={(e) => set("ein", e.target.value)} placeholder="12-3456789" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="brand-logo">Logo URL</Label>
          <Input id="brand-logo" data-testid="brand-logo-url" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="brand-msg">Thank-you message</Label>
          <Textarea id="brand-msg" data-testid="brand-thank-you" rows={3} value={form.thank_you_message} onChange={(e) => set("thank_you_message", e.target.value)} placeholder="A short personal note shown on every receipt." />
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-4 rounded-lg bg-[#F9FAFB] p-4">
        <div>
          <div className="text-sm font-bold text-ff-blue">Show "tax-deductible" line</div>
          <div className="text-xs text-ff-medium">Adds an IRS 501(c)(3) tax-deductible statement to receipts.</div>
        </div>
        <Switch checked={form.tax_line_enabled} onCheckedChange={(v) => set("tax_line_enabled", v)} data-testid="brand-tax-line" />
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="brand-save-button" className="bg-ff-blue text-white hover:bg-ff-blue/90">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Save branding
        </Button>
      </div>
    </div>
  );
}

function RecurringDonors() {
  const PAGE = 25;
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.get(`/pro/recurring-donors?limit=${PAGE}&offset=0`).then((r) => {
      setData(r.data); setRows(r.data.donors || []); setOffset(r.data.donors?.length || 0);
    }).catch(() => setData(null));
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: d } = await api.get(`/pro/recurring-donors?limit=${PAGE}&offset=${offset}`);
      setRows((prev) => [...prev, ...(d.donors || [])]);
      setOffset((o) => o + (d.donors?.length || 0));
      setData(d);
    } catch (_) {} finally { setLoadingMore(false); }
  };

  const exportCsv = async () => {
    let all = rows;
    try {
      const { data: d } = await api.get("/pro/recurring-donors?limit=100000&offset=0");
      all = d.donors || rows;
    } catch (_) {}
    if (!all.length) return;
    const header = ["Donor", "Email", "Monthly Amount", "Since", "Status", "Campaign"];
    const csvRows = all.map((d) => [d.donor_name, d.donor_email, d.amount, (d.since || "").slice(0, 10), d.status, d.campaign_title]);
    const csv = [header, ...csvRows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "recurring-donors.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Recurring donors exported.");
  };

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="pro-recurring-donors">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#ECFDF5] text-ff-green"><Users className="h-5 w-5" /></span>
          <div>
            <div className="font-heading font-bold text-ff-blue">Recurring donors</div>
            <div className="text-sm text-ff-medium">Your monthly supporters — the backbone of steady funding.</div>
          </div>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!rows.length} data-testid="donors-export-csv"
          className="border-ff-border text-ff-blue hover:border-ff-green hover:text-ff-green">
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {!data ? (
        <InlineLoader label="Loading donors" />
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3" data-testid="donors-summary">
            <Stat label="Active" value={data.summary.active} />
            <Stat label="MRR" value={money(data.summary.mrr)} accent="green" />
            <Stat label="Churn" value={`${data.summary.churn_pct}%`} accent="red" />
          </div>
          {rows.length === 0 ? (
            <div className="mt-4 rounded-lg border border-dashed border-ff-border p-6 text-center text-sm text-ff-medium">
              No recurring donors yet. Promote the "Monthly" option on your donate page to build recurring revenue.
            </div>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ff-border text-left text-[11px] font-bold uppercase tracking-wide text-ff-medium">
                    <th className="py-2 pr-4">Donor</th><th className="py-2 pr-4">Monthly</th>
                    <th className="py-2 pr-4">Since</th><th className="py-2 pr-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d, i) => (
                    <tr key={i} className="border-b border-ff-border/60" data-testid={`donor-row-${i}`}>
                      <td className="py-2.5 pr-4">
                        <div className="font-semibold text-ff-blue">{d.donor_name}</div>
                        <div className="text-xs text-ff-medium">{d.campaign_title}</div>
                      </td>
                      <td className="py-2.5 pr-4 font-bold text-ff-blue">{money(d.amount)}</td>
                      <td className="py-2.5 pr-4 text-ff-medium">{(d.since || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-4">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${d.status === "active" ? "bg-[#ECFDF5] text-ff-green" : "bg-[#FEF2F2] text-ff-red"}`}>
                          {d.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.has_more && (
                <div className="mt-4 flex justify-center">
                  <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="donors-load-more"
                    className="border-ff-border text-ff-blue hover:border-ff-blue">
                    {loadingMore ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Load more ({rows.length} of {data.total})
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent = "blue" }) {
  const color = accent === "green" ? "text-ff-green" : accent === "red" ? "text-ff-red" : "text-ff-blue";
  return (
    <div className="rounded-xl bg-[#F9FAFB] p-4 text-center">
      <div className={`font-heading text-xl font-extrabold ${color}`}>{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-ff-medium">{label}</div>
    </div>
  );
}
